import type { datadogLogs as datadogLogsSdk } from '@datadog/browser-logs'
import type { datadogRum as datadogRumSdk } from '@datadog/browser-rum'
import config from 'constants/config'
import { reportClientError } from './errorReportingService'

type DatadogLogsSdk = typeof datadogLogsSdk
type DatadogRumSdk = typeof datadogRumSdk

let initialized = false
let logsInitialized = false
let rumInitialized = false
let monitoringUnavailable = false

let datadogLogs: DatadogLogsSdk | undefined
let datadogRum: DatadogRumSdk | undefined
let latestUserId: string | number | undefined | null
let hasMonitoringUser = false

type DatadogBrowserConfig = {
  clientToken: string
  site: string
  service: string
  env: string
  version: string
}

// The SDKs now arrive asynchronously, so anything reported during startup would
// be dropped without somewhere to hold it. Bounded so that a load failure or a
// build with monitoring switched off cannot grow this without limit.
const MAX_BUFFERED_EVENTS = 100
const bufferedEvents: Array<() => void> = []

function runWhenLoaded(run: () => void) {
  if (logsInitialized || rumInitialized) {
    run()
    return
  }
  if (monitoringUnavailable) return
  if (bufferedEvents.length < MAX_BUFFERED_EVENTS) {
    bufferedEvents.push(run)
  }
}

function discardBufferedEvents() {
  monitoringUnavailable = true
  bufferedEvents.length = 0
}

function flushBufferedEvents() {
  const events = bufferedEvents.splice(0, bufferedEvents.length)
  for (const run of events) {
    try {
      run()
    } catch {
      // One bad event must not stop the rest of the buffer from flushing
    }
  }
}

function applyMonitoringUser(userId: string | number | undefined | null) {
  try {
    if (userId != null && userId !== '') {
      const user = { id: String(userId) }
      if (rumInitialized) datadogRum?.setUser(user)
      if (logsInitialized) datadogLogs?.setUser(user)
    } else {
      if (rumInitialized) datadogRum?.clearUser()
      if (logsInitialized) datadogLogs?.clearUser()
    }
  } catch {
    // Silently fail
  }
}

async function loadMonitoring(
  applicationId: string | undefined,
  rumEnabled: boolean,
  datadogConfig: DatadogBrowserConfig,
) {
  const { datadogLogs: logs } = await import('@datadog/browser-logs')
  logs.init({
    ...datadogConfig,
    forwardErrorsToLogs: false,
    sessionSampleRate: 100,
  })
  datadogLogs = logs
  logsInitialized = true
  flushBufferedEvents()

  if (rumEnabled && applicationId) {
    const [{ datadogRum: rum }, { reactPlugin }] = await Promise.all([
      import('@datadog/browser-rum'),
      import('@datadog/browser-rum-react'),
    ])

    rum.init({
      applicationId,
      ...datadogConfig,
      // Cost guard: keep RUM ingestion to 1% if it is enabled in the future.
      sessionSampleRate: 1,
      sessionReplaySampleRate: 0,
      defaultPrivacyLevel: 'mask-user-input',
      trackResources: true,
      trackUserInteractions: true,
      trackLongTasks: true,
      allowedTracingUrls: [],
      // Router view tracking requires Datadog's React Router v6 wrapper; this app uses a history router.
      plugins: [reactPlugin()],
    })
    datadogRum = rum
    rumInitialized = true

    if (hasMonitoringUser) applyMonitoringUser(latestUserId)
  }
}

export function initUserMonitoring() {
  if (initialized) return
  initialized = true

  const applicationId = config.DATADOG_RUM_APPLICATION_ID
  const clientToken = config.DATADOG_CLIENT_TOKEN
  const rumEnabled = config.DATADOG_RUM_ENABLED

  if (!clientToken) {
    discardBufferedEvents()
    return
  }

  const site = config.DATADOG_RUM_SITE || 'ap2.datadoghq.com'
  const service = 'www-le-customer'
  const env = config.APP_ENV || 'unknown'
  const commitSha = config.COMMIT_SHA || undefined
  const version = commitSha ? commitSha.substring(0, 7) : 'unknown'
  const datadogConfig = { clientToken, site, service, env, version }

  loadMonitoring(applicationId, Boolean(rumEnabled), datadogConfig).catch((error) => {
    if (logsInitialized) {
      flushBufferedEvents()
    } else {
      discardBufferedEvents()
    }
    try {
      reportClientError(error, { context: 'initUserMonitoring' })
    } catch {
      // Monitoring is unavailable and Sentry failed; don't crash the app
    }
  })
}

export function setMonitoringUser(userId: string | number | undefined | null) {
  if (typeof window === 'undefined') return

  latestUserId = userId
  hasMonitoringUser = true
  runWhenLoaded(() => applyMonitoringUser(userId))
}

export function reportLog(message: string, data: any, level: 'info' | 'warn' | 'error' = 'error') {
  if (typeof window === 'undefined') return

  const context = data != null && typeof data === 'object' ? data : { value: data }
  // Captured at the call site so a buffered report keeps a useful stack.
  const error = level === 'error' ? new Error(message) : undefined

  runWhenLoaded(() => {
    try {
      if (rumInitialized && error) {
        datadogRum?.addError(error, context)
      } else if (rumInitialized) {
        datadogRum?.addAction(message, { ...context, level })
      }

      if (logsInitialized) {
        datadogLogs?.logger[level](message, context)
      }
    } catch {
      try {
        reportClientError(new Error('reportLog: client logging failed'), {
          originalMessage: message,
          attemptedLevel: level,
        })
      } catch {
        // Both monitoring and Sentry failed; don't crash the app
      }
    }
  })
}

export function createLoggerWithContext(baseContext: Record<string, any>) {
  return (message: string, level: 'info' | 'warn' | 'error', additionalContext: Record<string, any> = {}) => {
    reportLog(message, { ...baseContext, ...additionalContext }, level)
  }
}
